import { AdjustmentsVerticalIcon } from "@heroicons/react/24/outline";
import { useSearchParams } from "@remix-run/react";
import { useState } from "react";
import { ClientOnly } from "remix-utils/client-only";

import { FeedFilters } from "~/types/feed-match";
import { useOptionalArtists } from "~/utils";

import { Button } from "../Button";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "../Drawer.client";

import { ArtistFilterItem } from "./ArtistFilterItem";

export function HomeFilters() {
  const artists = useOptionalArtists();
  const [isOpen, setIsOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeFilters, setActiveFilters] = useState<FeedFilters>(
    getFiltersFromSearch(searchParams),
  );
  const [pendingFilters, setPendingFilters] = useState<FeedFilters>(
    getFiltersFromSearch(searchParams),
  );

  function saveAndClose() {
    const newSearchParams = new URLSearchParams();
    const artistIds = pendingFilters.excludedArtistIds;
    if (artistIds && artistIds.length > 0) {
      newSearchParams.set("artistIds", artistIds.join(","));
    }
    setSearchParams(newSearchParams.toString());
    setIsOpen(false);
    setActiveFilters(pendingFilters);
  }

  function onArtistClick(artistId: string) {
    setPendingFilters((prev) => {
      const excludedSet = new Set(prev.excludedArtistIds ?? []);
      if (excludedSet.has(artistId)) {
        excludedSet.delete(artistId);
      } else {
        excludedSet.add(artistId);
      }
      return { ...prev, excludedArtistIds: Array.from(excludedSet) };
    });
  }

  function cancel() {
    setPendingFilters(activeFilters);
    setIsOpen(false);
  }

  function handleOnOpenChange(value: boolean) {
    if (!value) {
      cancel();
      return;
    }
    setIsOpen(value);
  }

  if (!artists) return null;

  return (
    <ClientOnly>
      {() => (
        <Drawer open={isOpen} onOpenChange={handleOnOpenChange}>
          <DrawerTrigger asChild>
            {hasActiveFilters(activeFilters) ? (
              <Button
                variant="solid"
                className="text-black bg-white h-8 px-3.5"
              >
                filters active
              </Button>
            ) : (
              <AdjustmentsVerticalIcon className="text-white h-6 w-6 transition-all hover:scale-105 cursor-pointer" />
            )}
          </DrawerTrigger>
          <DrawerContent className="h-5/6 items-center focus-visible:outline-none focus-visible:ring-0 focus-visible:border-0">
            <div className="px-2 max-w-96 w-full">
              <DrawerHeader>
                <DrawerTitle>filters</DrawerTitle>
              </DrawerHeader>
              <div className="gap-3 flex flex-col">
                <div className="text-gray-400">artists</div>
                <div className="grid grid-cols-4 gap-2 w-fit">
                  {artists.map((artist) => (
                    <ArtistFilterItem
                      key={artist.id}
                      artist={artist}
                      isSelected={
                        !pendingFilters.excludedArtistIds?.includes(artist.id)
                      }
                      onClick={onArtistClick}
                    />
                  ))}
                </div>
              </div>
              <DrawerFooter className="flex-row justify-between px-0">
                <Button variant="ghost" className="-ml-4" onClick={cancel}>
                  cancel
                </Button>
                <Button onClick={saveAndClose}>apply</Button>
              </DrawerFooter>
            </div>
          </DrawerContent>
        </Drawer>
      )}
    </ClientOnly>
  );
}

function hasActiveFilters(filters: FeedFilters) {
  return !!filters.excludedArtistIds?.length;
}

function getFiltersFromSearch(searchParams: URLSearchParams): FeedFilters {
  const artistIds = searchParams.get("artistIds")?.split(",");
  if (!artistIds || artistIds.length === 0) return {};
  return { excludedArtistIds: artistIds };
}
